import { StatusIcon, toneText, type Tone } from "@/components/ui/status-icon";
import { fill, useWordsVersion } from "@/api/words";
import { formatDayNear, formatTime } from "@/money/format";

import { uk } from "../i18n/uk";

import type { BackupAnswer, FreshAnswer } from "./api";
import { Freshness } from "./Freshness";
import { pathOf } from "./nav-paths";
import type { ShellState } from "./shell";

// Строка состояния внизу окна на всю ширину (F-013, решение 066, картинка Σ): одна строка,
// половины по краям, внутри без переноса. Слева — свежесть данных DnTrade
// («DnTrade: свіжо · 22:47» / «застаріло · N год» / сбой синхронизации со
// ссылкой на «Стан даних»), справа — резервная копия. Свежесть и её слова — `Freshness`
// (слово индикатора — код случая сервера); копия — ответ `GET /api/shell/status`.
//
// ⚠ Сбой копии и сбой синхронизации — ещё и дела плашки «Потребує уваги» (тот же
// источник, `api/attention.py`): здесь они остаются тихим состоянием — значок и
// слово, без `alert`, чтобы одну беду не объявляли дважды.
//
// ⚠ Копия — сведение «Стану даних»: у кого он закрыт, правой половины нет вовсе
// (`hidden`). Журнал копий не ответил — «не знаю», а не «копії немає» (правило 10).

function momentOf(iso: string): string {
  const at = new Date(iso);
  return `${formatDayNear(at)}, ${formatTime(at)}`;
}

function BackupLine({ answer }: { readonly answer: BackupAnswer | null }) {
  // Ответа ещё нет — место держит скелет той же высоты: низ меню не прыгает, когда строка придёт.
  if (answer === null) {
    return (
      <div aria-hidden="true" className="flex h-4 items-center gap-xs">
        <span className="size-dot rounded-full bg-skeleton" />
        <span className="h-xs w-3xl rounded bg-skeleton" />
      </div>
    );
  }
  if (answer.kind === "hidden") return null;
  let tone: Tone;
  let text: string;
  let sr: string | null = null;
  // «Резервна копія: 02.10, 12:00» и «Резервна копія: не знаю» — подпись и значение; сбой — одна фраза.
  let labelled = false;
  if (answer.kind === "silent") {
    labelled = true;
    tone = "unknown";
    text = uk.status.backupUnknown;
  } else {
    const { state, at } = answer.row;
    if (state === "ok" && at !== null) {
      tone = "ok";
      labelled = true;
      text = fill(uk.status.backupOk, { when: momentOf(at) });
      sr = uk.status.backupOkSr;
    } else if (state === "failed") {
      tone = "alarm";
      text = uk.status.backupFailed;
    } else if (state === "none") {
      tone = "warn";
      text = uk.status.backupNone;
    } else {
      tone = "unknown";
      labelled = true;
      text = uk.status.backupUnknown;
    }
  }
  return (
    // Тихо (`status`): вслух сбой копии говорит плашка «Потребує уваги».
    // Как и слева: подпись `ink-3`, значение `ink-2`; сбой — фразой в цвете тона, без подписи.
    <p role="status" className="flex min-w-0 items-center gap-xs whitespace-nowrap text-xs">
      <StatusIcon tone={tone} muted={tone === "ok"} />
      {labelled ? <span className="shrink-0 text-ink-3">{uk.status.backup}:</span> : null}
      <span data-numbers title={text} className={`min-w-0 truncate ${labelled ? "text-ink-2" : toneText(tone)}`}>
        {text}
      </span>
      {sr === null ? null : <span className="sr-only"> · {sr}</span>}
    </p>
  );
}

/**
 * Строка состояния — внизу окна на всю ширину (картинка Σ): слева свежесть данных DnTrade
 * с «?», справа резервная копия. На узком окне (меню за кнопкой) она там же, внизу.
 */
export function StatusBar({ shell }: { readonly shell: ShellState | null }) {
  const me = shell?.me ?? null;
  const granted = me !== null && me !== false && Object.prototype.hasOwnProperty.call(me.surfaces, "S-10");
  if (shell === null || me === null || me === false) return null;
  return (
    <footer className="sticky bottom-0 z-[var(--z-sticky,10)] flex h-statusbar items-center border-t border-line-subtle bg-chrome px-md">
      <div className="w-full">
        <StatusLine fresh={shell.fresh} backup={shell.backup} at={granted ? pathOf("S-10") : undefined} closed={!granted} />
      </div>
    </footer>
  );
}

export function StatusLine({
  fresh,
  backup,
  at,
  closed,
}: {
  readonly fresh: FreshAnswer | null;
  readonly backup: BackupAnswer | null;
  /** Адрес «Стану даних», если он человеку выдан. */
  readonly at: string | undefined;
  readonly closed: boolean;
}) {
  useWordsVersion();
  return (
    <div role="group" aria-label={uk.status.label} className="flex flex-wrap items-center justify-between gap-x-md gap-y-2xs">
      <Freshness answer={fresh} at={at} closed={closed} />
      <BackupLine answer={backup} />
    </div>
  );
}
